import { Check, Plus, Trash2 } from "lucide-react";
import { AutoTextarea, TextField } from "@/components/tools/fields";

const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
export const blankRows = (n) => Array.from({ length: n }, (_, i) => ({ id: `r${i + 1}` }));

export const SubHead = ({ letter, title, children }) => (
  <div className="mb-5 mt-12 flex items-baseline gap-3 border-b-2 border-ink pb-3 first:mt-0">
    <span className="font-display text-2xl italic text-brand" aria-hidden="true">{letter}</span>
    <div>
      <h4 className="font-display text-xl font-medium sm:text-2xl">{title}</h4>
      {children && <p className="mt-1 text-sm text-ink-soft">{children}</p>}
    </div>
  </div>
);

const Cell = ({ col, row, i, name, onChange }) => {
  const id = `${name}-${row.id}-${col.k}`;
  const testid = `${name}-row-${i}-${col.k}`;
  const label = <label htmlFor={id} className="field-label">{col.label}<span className="sr-only">, row {i + 1}</span></label>;
  if (col.type === "select") {
    return (
      <div className="flex flex-col gap-1.5">
        {label}
        <select id={id} value={row[col.k] ?? ""} onChange={(e) => onChange(e.target.value)} className="field-input" data-testid={testid}>
          <option value="">Choose</option>
          {col.options.map((o) => <option key={o}>{o}</option>)}
        </select>
      </div>
    );
  }
  if (col.type === "date") return <TextField id={id} label={col.label} type="date" value={row[col.k]} onChange={onChange} testid={testid} />;
  return <AutoTextarea id={id} label={col.label} rows={1} value={row[col.k]} onChange={onChange} testid={testid} />;
};

export const RowsEditor = ({ name, rows, setRows, cols, addLabel = "Add a row", grid = "sm:grid-cols-2 lg:grid-cols-4" }) => {
  const list = Array.isArray(rows) ? rows : [];
  return (
    <div>
      <ol className="space-y-3">
        {list.map((r, i) => (
          <li key={r.id} className="avoid-break rounded-2xl border-2 border-line bg-surface p-4" data-testid={`${name}-row-${i}`}>
            <div className="mb-3 flex items-center justify-between">
              <span className="eyebrow">Row {i + 1}</span>
              <button type="button" aria-label={`Remove row ${i + 1}`} data-testid={`${name}-remove-${i}`}
                onClick={() => setRows(list.filter((x) => x.id !== r.id))}
                className="no-print flex h-10 w-10 items-center justify-center rounded-full border-2 border-line hover:border-ink">
                <Trash2 size={15} aria-hidden />
              </button>
            </div>
            <div className={`grid gap-3 ${grid}`}>
              {cols.map((c) => (
                <Cell key={c.k} col={c} row={r} i={i} name={name}
                  onChange={(v) => setRows(list.map((x) => (x.id === r.id ? { ...x, [c.k]: v } : x)))} />
              ))}
            </div>
          </li>
        ))}
      </ol>
      <button type="button" className="btn btn-ghost btn-sm no-print mt-4" data-testid={`${name}-add`} onClick={() => setRows([...list, { id: uid() }])}>
        <Plus size={14} aria-hidden /> {addLabel}
      </button>
    </div>
  );
};

export const Checklist = ({ name, items, value = {}, onChange }) => (
  <ol className="space-y-3">
    {items.map((s, i) => {
      const st = value[s.id] || {};
      const set = (patch) => onChange({ ...value, [s.id]: { ...st, ...patch } });
      return (
        <li key={s.id} className={`avoid-break rounded-2xl border-2 p-4 transition-colors sm:p-5 ${st.done ? "border-brand bg-surface" : "border-line"}`}>
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_170px_minmax(0,1fr)] lg:items-start">
            <label className="flex cursor-pointer items-start gap-3" data-testid={`${name}-${s.id}`}>
              <input type="checkbox" checked={!!st.done} onChange={(e) => set({ done: e.target.checked })} className="peer sr-only" />
              <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border-2 peer-focus-visible:outline peer-focus-visible:outline-[3px] peer-focus-visible:outline-offset-2 ${st.done ? "border-brand bg-brand text-brand-fg" : "border-ink-soft"}`} aria-hidden="true">
                {st.done && <Check size={16} strokeWidth={3} />}
              </span>
              <span>
                <span className="block font-bold"><span className="font-display italic text-ink-soft">{i + 1}.</span> {s.title}</span>
                {s.hint && <span className="block text-sm text-ink-soft">{s.hint}</span>}
              </span>
            </label>
            <TextField id={`${name}-${s.id}-date`} label="Date done" type="date" value={st.date} onChange={(v) => set({ date: v })} testid={`${name}-${s.id}-date`} />
            <AutoTextarea id={`${name}-${s.id}-notes`} label="Notes" rows={1} value={st.notes} onChange={(v) => set({ notes: v })} testid={`${name}-${s.id}-notes`} />
          </div>
        </li>
      );
    })}
  </ol>
);

export const Progress = ({ done, total, label, testid }) => (
  <div className="mb-5" aria-live="polite">
    <div className="flex items-baseline justify-between">
      <p className="font-bold" data-testid={testid}>{done} of {total} {label}</p>
      <p className="font-display text-2xl">{Math.round((done / total) * 100)}%</p>
    </div>
    <div className="mt-2 h-3 overflow-hidden rounded-full bg-line" role="progressbar" aria-valuemin={0} aria-valuemax={total} aria-valuenow={done} aria-label={label}>
      <div className="h-full rounded-full bg-brand transition-[width] duration-700" style={{ width: `${(done / total) * 100}%` }} />
    </div>
  </div>
);
